import React, { ReactNode, useEffect, useState } from 'react';
import { graphql, useLazyLoadQuery, usePaginationFragment } from 'react-relay';
import { toLocalID } from 'src/shared/helpers';
import { SkeletonBlock } from '../skeleton';
import type { PricingGroupSetup } from './types';
import type { AgentPricingAssignmentsProductsQuery } from './__generated__/AgentPricingAssignmentsProductsQuery.graphql';
import type { AgentPricingAssignmentsQuery } from './__generated__/AgentPricingAssignmentsQuery.graphql';
import type { AgentPricingAssignmentsPaginationQuery } from './__generated__/AgentPricingAssignmentsPaginationQuery.graphql';
import type { AgentPricingAssignments_printTypes$key } from './__generated__/AgentPricingAssignments_printTypes.graphql';

export type PricingAssignmentTarget = { id: number; name: string; code?: string | null };

/** Display only current catalogue entries, in assignment order; never invent ID-based labels. */
export const resolvePricingAssignmentTargets = (ids: readonly number[], catalogue: readonly PricingAssignmentTarget[]) => {
    const byId = new Map(catalogue.map((target) => [target.id, target]));
    return [...new Set(ids)].flatMap((id) => {
        const target = byId.get(id);
        return target?.name.trim() ? [target] : [];
    });
};

type TargetsProps = {
    ids: readonly number[];
    children: (targets: readonly PricingAssignmentTarget[]) => ReactNode;
};

const ProductTargets = ({ ids, children }: TargetsProps) => {
    // Agent mutations use REST: refresh names rather than relying on an old Relay catalogue.
    const data = useLazyLoadQuery<AgentPricingAssignmentsProductsQuery>(
        graphql`
            query AgentPricingAssignmentsProductsQuery {
                modelsHierarchy {
                    productID
                    name
                    code
                }
            }
        `,
        {},
        { fetchPolicy: 'network-only', fetchKey: ids.join(',') }
    );
    const catalogue = data.modelsHierarchy.flatMap((product) => {
        const id = toLocalID(product.productID);
        return id == null ? [] : [{ id, name: product.name, code: product.code }];
    });
    return <>{children(resolvePricingAssignmentTargets(ids, catalogue))}</>;
};

const PrintingMethodTargets = ({ ids, children }: TargetsProps) => {
    const query = useLazyLoadQuery<AgentPricingAssignmentsQuery>(
        graphql`
            query AgentPricingAssignmentsQuery {
                ...AgentPricingAssignments_printTypes
            }
        `,
        {},
        { fetchPolicy: 'network-only', fetchKey: ids.join(',') }
    );
    const { data, hasNext, isLoadingNext, loadNext } = usePaginationFragment<AgentPricingAssignmentsPaginationQuery, AgentPricingAssignments_printTypes$key>(
        graphql`
            fragment AgentPricingAssignments_printTypes on Query
            @refetchable(queryName: "AgentPricingAssignmentsPaginationQuery")
            @argumentDefinitions(count: { type: "Int", defaultValue: 100 }, cursor: { type: "String", defaultValue: null }) {
                printTypes(first: $count, after: $cursor) @connection(key: "AgentPricingAssignments_printTypes") {
                    edges {
                        node {
                            id
                            name
                            printTypeID
                        }
                    }
                }
            }
        `,
        query
    );
    const [error, setError] = useState<Error | null>(null);
    const targets = resolvePricingAssignmentTargets(
        ids,
        (data.printTypes?.edges ?? []).map(({ node }) => ({ id: node.printTypeID, name: node.name }))
    );
    const needsMore = targets.length < new Set(ids).size && hasNext;
    useEffect(() => {
        if (needsMore && !isLoadingNext && !error) {
            loadNext(100, { onComplete: (failure) => failure && setError(failure) });
        }
    }, [needsMore, isLoadingNext, loadNext, error]);
    if (error) throw error;
    // Do not report a partial count while a referenced printing method may be on a later page.
    if (needsMore) return <SkeletonBlock $width='100%' $height='44px' />;
    return <>{children(targets)}</>;
};

export const AgentPricingAssignments = ({
    assignments,
    children
}: {
    assignments: NonNullable<PricingGroupSetup['assignments']>;
    children: TargetsProps['children'];
}) => {
    const ids = assignments.targetIds ?? [];
    if (ids.length === 0) return null;
    const key = `${assignments.targetType}:${ids.join(',')}`;
    if (assignments.targetType === 'products') return <ProductTargets key={key} ids={ids} children={children} />;
    if (assignments.targetType === 'printingMethods') return <PrintingMethodTargets key={key} ids={ids} children={children} />;
    return null;
};
